import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Loader2, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { MastercardMark } from './mastercard-mark';

// The compensation card, drawn as the thing it is: a card. An operator reading
// details out to a passenger at a desk — or a passenger looking at their own
// phone — recognises a card face instantly and reads a grouped number far more
// reliably than a form field.
//
// THE PAN IS NEVER OURS. `apps/wallet` deliberately models no PAN, CVV or expiry
// (wallet TDS §10 — "the PCI boundary is the iframe"): the service holds `last4`
// and `network` and nothing else, and full details are shown by mounting the
// ISSUER'S hosted iframe against a short-lived reveal token
// (`GET /wallet/cards/:urn/reveal-token`). So this component takes no `pan`
// prop — it cannot leak what it is never given. The parent passes the mounted
// iframe as `secureDetails`, and the face makes room for it.
//
// CARD PERSONALISATION. The face carries no cardholder name — a reference (the
// PNR) sits where a nominated card prints one. NOTE: the wallet backend TODAY
// issues NOMINATED cards (it freezes a KYC-backed `IssuedCardholder` per card);
// a bearer/non-personalised product is a backend change, not just this face.
//
// The face stays dark in both app themes on purpose — it depicts a physical
// object, so it does not follow the surrounding light/dark chrome.

export type CardNetwork = 'MASTERCARD' | 'VISA' | string;

/** The wallet domain's `IssuedCardStatus` (apps/wallet issued.card.schema.ts). */
export type CardStatus =
  | 'PENDING'
  | 'ACTIVE'
  | 'BLOCKED'
  | 'DISABLED'
  | 'FAILED'
  | 'FAILED_COMPENSATED';

interface VirtualCardProps {
  /** Card network — drives the acceptance mark. */
  network?: CardNetwork | undefined;
  /** Last four digits: the only card-number data this system holds. */
  last4?: string | undefined;
  /**
   * Lifecycle status. ABSENT = no card exists yet, and the face renders as a
   * greyed-out placeholder: no brand mark (we do not know the network until an
   * issuer picks one), no live plastic. A full-colour card face must never
   * appear for a passenger who has not been given one.
   */
  status?: CardStatus | undefined;
  /** The loaded value, pre-formatted with its currency (e.g. "$35.00"). */
  amountLabel?: string | undefined;
  /** Spendable balance, when it differs from the loaded amount. */
  balanceLabel?: string | undefined;
  /** Printed where a nominated card prints a name — typically the PNR. */
  reference?: string | undefined;
  /** Issuer wordmark, top-left. Per-tenant. */
  issuerLabel?: string;
  /**
   * The issuer's hosted reveal iframe, already mounted by the parent against a
   * live reveal token. When present it covers the number/validity zone — the
   * only place full details ever appear, rendered by the issuer, not by us.
   */
  secureDetails?: ReactNode;
  /** Starts the audited reveal. Omit to render a face with no reveal control. */
  onReveal?: (() => void) | undefined;
  /** True while the reveal token is being fetched. */
  revealing?: boolean;
  /** Tears the secure iframe back down. */
  onHide?: (() => void) | undefined;
  className?: string;
}

/** The masked face: three dot groups then the real last four. */
function maskedPan(last4?: string): string {
  return `•••• •••• •••• ${last4 ?? '••••'}`;
}

// Holds KEYS, never language — the face renders them through `t()`.
const STATUS_OVERLAY: Partial<
  Record<CardStatus, { labelKey: string; hintKey: string; tone: 'warn' | 'alert' }>
> = {
  BLOCKED: {
    labelKey: 'card.status.blocked.label',
    hintKey: 'card.status.blocked.hint',
    tone: 'warn',
  },
  DISABLED: {
    labelKey: 'card.status.disabled.label',
    hintKey: 'card.status.disabled.hint',
    tone: 'alert',
  },
  FAILED: {
    labelKey: 'card.status.failed.label',
    hintKey: 'card.status.failed.hint',
    tone: 'alert',
  },
  FAILED_COMPENSATED: {
    labelKey: 'card.status.failedCompensated.label',
    hintKey: 'card.status.failedCompensated.hint',
    tone: 'alert',
  },
};

/** No card yet — a quiet placeholder in the shape of one, never a live face. */
function EmptyCardFace({
  amountLabel,
  className,
}: {
  amountLabel?: string | undefined;
  className?: string;
}) {
  const { t } = useTranslation('admin');
  return (
    <div className={cn('w-full max-w-[21rem]', className)} data-testid="virtual-card">
      <div
        className="relative flex aspect-[1.586] flex-col justify-between rounded-xl border border-dashed border-border bg-muted/40 p-4"
        data-card-status="NONE"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground/70">
              NEXA
            </div>
            <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
              {t('card.face.prepaid')}
            </div>
          </div>
          {amountLabel ? (
            <div className="text-right">
              <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
                {t('card.face.entitled')}
              </div>
              <div className="text-lg font-bold leading-none tabular-nums text-muted-foreground">
                {amountLabel}
              </div>
            </div>
          ) : null}
        </div>

        <div className="font-mono text-[1.05rem] leading-none tracking-[0.1em] text-muted-foreground/40">
          •••• •••• •••• ••••
        </div>

        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t('card.face.noCard')}
        </div>
      </div>
    </div>
  );
}

export function VirtualCard({
  network = 'MASTERCARD',
  last4,
  status,
  amountLabel,
  balanceLabel,
  reference,
  issuerLabel = 'NEXA',
  secureDetails,
  onReveal,
  revealing = false,
  onHide,
  className,
}: VirtualCardProps) {
  const { t } = useTranslation('admin');
  // No status = no card. Never dress an un-issued entitlement as a real card.
  if (!status) {
    return <EmptyCardFace {...(amountLabel ? { amountLabel } : {})} {...(className ? { className } : {})} />;
  }

  const pending = status === 'PENDING';
  const overlay = STATUS_OVERLAY[status];
  const revealed = !!secureDetails;

  return (
    <div className={cn('w-full max-w-[21rem]', className)} data-testid="virtual-card">
      <div
        className={cn(
          'relative flex aspect-[1.586] flex-col justify-between overflow-hidden rounded-xl p-4 shadow-lg',
          // Depicts a physical object → fixed dark face in both themes.
          'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white',
          'ring-1 ring-inset ring-white/10',
        )}
        data-card-status={status}
      >
        {/* Sheen — the light catching the plastic. */}
        <span
          className="pointer-events-none absolute -inset-x-8 -top-24 h-48 rotate-12 bg-gradient-to-b from-white/[0.09] to-transparent"
          aria-hidden
        />

        {/* Top: issuer + the loaded value. */}
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em]">{issuerLabel}</div>
            <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white/60">
              {t('card.face.prepaid')}
            </div>
          </div>
          {amountLabel ? (
            <div className="text-right">
              <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-white/60">
                {balanceLabel ? t('card.face.loaded') : t('card.face.value')}
              </div>
              <div
                className="text-lg font-bold leading-none tabular-nums"
                data-testid="virtual-card-amount"
              >
                {amountLabel}
              </div>
              {balanceLabel ? (
                <div className="mt-0.5 text-[10px] tabular-nums text-white/70">
                  {t('card.face.availableSuffix', { amount: balanceLabel })}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* The number — masked, always. Full details only ever arrive as the
            issuer's own iframe, which takes this zone over when mounted. */}
        <div className="relative">
          {revealed ? (
            <div className="min-h-[2.5rem]" data-testid="virtual-card-secure-frame">
              {secureDetails}
            </div>
          ) : (
            <div
              className="font-mono text-[1.05rem] leading-none tracking-[0.1em] tabular-nums"
              data-testid="virtual-card-pan"
            >
              {maskedPan(last4)}
            </div>
          )}
        </div>

        {/* Bottom: validity placeholders + the network mark. The real validity
            lives inside the issuer frame; we never hold it. */}
        <div className="relative flex items-end justify-between gap-4">
          {revealed ? (
            <span className="text-[10px] uppercase tracking-[0.14em] text-white/50">
              {t('card.face.secure')}
            </span>
          ) : (
            <div className="flex items-end gap-4">
              <div>
                <div className="text-[8px] font-semibold uppercase tracking-[0.14em] text-white/60">
                  {t('card.face.validThru')}
                </div>
                <div className="mt-0.5 font-mono text-xs tabular-nums">••/••</div>
              </div>
              <div>
                <div className="text-[8px] font-semibold uppercase tracking-[0.14em] text-white/60">
                  {t('card.face.cvc')}
                </div>
                <div className="mt-0.5 font-mono text-xs tabular-nums">•••</div>
              </div>
            </div>
          )}
          {network === 'MASTERCARD' ? (
            <MastercardMark height={28} className="shrink-0 text-white/90" />
          ) : network === 'VISA' ? (
            <span className="text-lg font-bold italic tracking-wide text-white/90">VISA</span>
          ) : null}
        </div>

        {/* The holder line a nominated card would carry. */}
        <div className="relative flex items-center justify-between gap-3 text-[9px] uppercase tracking-[0.14em] text-white/70">
          <span data-testid="virtual-card-holder">{t('card.face.nonPersonalised')}</span>
          {reference ? (
            <span className="font-mono tracking-normal text-white/60">
              {t('card.face.ref')} {reference}
            </span>
          ) : null}
        </div>

        {/* Issuance in flight, or a card that cannot be used. */}
        {pending ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-950/70 backdrop-blur-[1px]"
            data-testid="virtual-card-pending"
          >
            <Loader2 className="h-6 w-6 animate-spin text-white/80" aria-hidden />
            <p className="text-xs font-medium text-white/80">{t('card.face.issuing')}</p>
          </div>
        ) : overlay ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-950/80 px-6 text-center backdrop-blur-[1px]"
            data-testid="virtual-card-overlay"
          >
            <TriangleAlert
              className={cn(
                'h-5 w-5',
                overlay.tone === 'alert' ? 'text-rose-400' : 'text-amber-400',
              )}
              aria-hidden
            />
            <p className="text-base font-bold uppercase tracking-[0.14em] text-white">
              {t(overlay.labelKey)}
            </p>
            <p className="text-xs text-white/70">{t(overlay.hintKey)}</p>
          </div>
        ) : null}
      </div>

      {/* Controls sit OUTSIDE the plastic — the face stays a faithful card. */}
      {onReveal || onHide ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {revealed ? (
            onHide ? (
              <button
                type="button"
                onClick={onHide}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
                data-testid="virtual-card-hide"
              >
                <EyeOff className="h-3.5 w-3.5" aria-hidden /> {t('card.face.hideDetails')}
              </button>
            ) : null
          ) : onReveal ? (
            <button
              type="button"
              onClick={onReveal}
              disabled={revealing || pending || !!overlay}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-50"
              title={t('card.face.showDetailsTitle')}
              data-testid="virtual-card-reveal"
            >
              {revealing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Eye className="h-3.5 w-3.5" aria-hidden />
              )}
              {t('card.face.showDetails')}
            </button>
          ) : null}
          <span className="text-[11px] text-muted-foreground">{t('card.face.auditNote')}</span>
        </div>
      ) : null}
    </div>
  );
}
